import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { SearchBox } from "@/components/SearchBox";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Clock, CalendarClock, ChevronRight, CheckCircle2, AlertCircle, BellRing } from "lucide-react";
import { SERVICES } from "@/lib/services";
import { AbsenceBands, hasFullDayAbsence } from "@/components/AgendaAbsence";

const DAYS = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];
const slugOf = (key) => Object.keys(SERVICES).find((s) => SERVICES[s].key === key) || "";
const svcLabel = (key) => SERVICES[slugOf(key)]?.label || key;
const iconOf = (key) => SERVICES[slugOf(key)]?.icon || CalendarClock;

async function solicit(workerId, setBusy) {
  setBusy(workerId);
  try {
    const { data } = await api.post(`/office/agenda/${workerId}/solicit`);
    if (data?.already) toast.info("Sollecito già inviato (in attesa di lettura dal lavoratore)");
    else toast.success("Sollecito inviato al lavoratore");
  } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Sollecito non riuscito"); }
  finally { setBusy(null); }
}

function StatusBadge({ compiled }) {
  return compiled ? (
    <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px] gap-1"><CheckCircle2 className="h-3 w-3" /> Agenda compilata</Badge>
  ) : (
    <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px] gap-1"><AlertCircle className="h-3 w-3" /> Agenda non compilata</Badge>
  );
}

function UpdatedBadge() {
  return <Badge className="bg-[#6D5FB8] text-white border-0 text-[10px] gap-1" data-testid="agenda-updated-badge"><CalendarClock className="h-3 w-3" /> Aggiornato</Badge>;
}

function TabPill({ active, onClick, children, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`px-3.5 py-1.5 rounded-full text-sm font-semibold border transition-all ${active ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-slate-200 hover:border-primary/40"}`}>
      {children}
    </button>
  );
}

// Agenda settimanale in sola lettura (riusa layout 7 giorni + card voce dell'Agenda Lavoratore).
function ReadOnlyAgenda({ slots, absences = {}, highlightDow = null }) {
  const hlRef = useRef(null);
  useEffect(() => {
    if (highlightDow != null && hlRef.current) {
      try { hlRef.current.scrollIntoView({ behavior: "smooth", block: "center" }); } catch (_) {}
    }
  }, [highlightDow]);
  const byDay = (dow) => {
    const out = [];
    slots.forEach((s) => {
      if (s.day_of_week === dow) out.push({ ...s, _disp: "start", _from: s.start_time, _to: s.overnight ? "24:00" : s.end_time });
      if (s.overnight && ((s.day_of_week + 1) % 7) === dow) out.push({ ...s, _disp: "end", _from: "00:00", _to: s.end_time });
    });
    return out.sort((a, b) => a._from.localeCompare(b._from));
  };
  return (
    <div className="space-y-4" data-testid="office-agenda-days">
      {DAYS.map((dname, dow) => {
        const items = byDay(dow);
        const dayAbsences = absences[dow] || [];
        const dimmed = hasFullDayAbsence(dayAbsences);
        const highlighted = highlightDow === dow;
        return (
          <section key={dow} ref={highlighted ? hlRef : null}
            className={`rounded-2xl bg-white border shadow-sm overflow-hidden transition-all ${highlighted ? "border-[#6D5FB8] ring-2 ring-[#6D5FB8]/50 shadow-md" : "border-slate-200"}`}
            data-testid={`office-agenda-day-${dow}`} data-highlighted={highlighted ? "true" : undefined}>
            <div className={`px-4 py-3 border-b flex items-center justify-between gap-2 ${highlighted ? "bg-[#EDEBF7] border-[#D9D4F0]" : "bg-[#F4F3FA] border-[#E5E1F3]"}`}>
              <h3 className="font-heading font-bold text-[#4A4470]">{dname}</h3>
              {highlighted && (
                <span data-testid="office-agenda-day-highlight-chip"
                  className="inline-flex items-center gap-1 rounded-full bg-[#6D5FB8] text-white text-[10px] font-semibold px-2 py-0.5">
                  <CalendarClock className="h-3 w-3" /> Assenza selezionata
                </span>
              )}
            </div>
            <div className="p-3">
              <AbsenceBands absences={dayAbsences} />
              <div className="space-y-2">
              {items.length === 0 ? (
                <p className="text-sm text-slate-400 px-1 py-1.5">Nessun servizio programmato</p>
              ) : items.map((s) => {
                const Icon = iconOf(s.service_type);
                return (
                  <div key={`${s.id}-${s._disp}`} className={`rounded-xl border border-slate-200 p-3 flex items-center gap-3 ${dimmed ? "opacity-50" : ""}`} data-testid={`office-agenda-slot-${s.id}-${s._disp}`}>
                    <div className="h-9 w-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0"><Icon className="h-4 w-4 text-primary" /></div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center gap-1 text-sm font-semibold text-slate-800"><Clock className="h-3.5 w-3.5 text-slate-400" />{s._from}–{s._to}</span>
                        {s._disp === "end" && <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px]">dal giorno precedente</Badge>}
                        {s._disp === "start" && s.overnight && <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px]">prosegue domani</Badge>}
                      </div>
                      <p className="text-sm font-medium text-slate-700 truncate">{svcLabel(s.service_type)}{s.role_or_task ? ` · ${s.role_or_task}` : ""}</p>
                      <p className="text-xs text-slate-500 truncate">{s.titolo}{s.sottotitolo ? ` · ${s.sottotitolo}` : ""}</p>
                    </div>
                  </div>
                );
              })}
              </div>
            </div>
          </section>
        );
      })}
    </div>
  );
}

function PerLavoratore({ onSeen, deepLink, onDeepLinkDone }) {
  const [workers, setWorkers] = useState([]);
  const [search, setSearch] = useState("");
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [selected, setSelected] = useState(null);
  const [slots, setSlots] = useState([]);
  const [absences, setAbsences] = useState({});
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  const [highlightDow, setHighlightDow] = useState(null);

  useEffect(() => {
    api.get("/office/agenda/workers").then((r) => setWorkers(r.data || [])).catch(() => {});
  }, []);

  const openWorker = async (w) => {
    setSelected(w); setLoading(true); setSlots([]); setAbsences({});
    try {
      const [sl, ab] = await Promise.all([
        api.get(`/office/agenda/slots?worker_id=${w.id}`),
        api.get(`/office/agenda/absences?worker_id=${w.id}`),
      ]);
      setSlots(sl.data || []);
      const m = {}; (ab.data?.days || []).forEach((d) => { m[d.dow] = d.absences || []; });
      setAbsences(m);
      if (w.updated) {
        setWorkers((ws) => ws.map((x) => x.id === w.id ? { ...x, updated: false } : x));
        onSeen && onSeen();
      }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Errore caricamento agenda"); }
    finally { setLoading(false); }
  };

  // Deep-link dall'Agenda Assenze: apre direttamente il lavoratore selezionato ed evidenzia
  // il giorno (giorno della settimana) dell'assenza da cui si proviene. Riusa openWorker esistente.
  useEffect(() => {
    if (!deepLink?.workerId || workers.length === 0) return;
    const w = workers.find((x) => x.id === deepLink.workerId);
    if (!w) { onDeepLinkDone && onDeepLinkDone(); return; }
    let dow = null;
    if (deepLink.date) {
      const [y, mo, da] = deepLink.date.split("-").map(Number);
      const js = new Date(y, (mo || 1) - 1, da || 1).getDay(); // 0=Dom..6=Sab
      dow = (js + 6) % 7; // 0=Lun..6=Dom
    }
    setHighlightDow(dow);
    openWorker(w);
    onDeepLinkDone && onDeepLinkDone();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLink, workers]);

  const filtered = workers
    .filter((w) => `${w.nome} ${w.cognome}`.toLowerCase().includes(search.toLowerCase()))
    .filter((w) => !onlyMissing || !w.compiled);
  const missingCount = workers.filter((w) => !w.compiled).length;

  if (selected) {
    return (
      <div data-testid="office-agenda-worker-view">
        <button onClick={() => { setSelected(null); setSlots([]); setHighlightDow(null); }} data-testid="office-agenda-worker-back"
          className="inline-flex items-center text-sm text-slate-500 hover:text-primary mb-4">
          ← Cambia lavoratore
        </button>
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-[#EDEBF7] flex items-center justify-center"><CalendarClock className="h-5 w-5 text-[#6D5FB8]" /></div>
            <div>
              <p className="font-heading text-lg font-bold text-slate-900">{selected.nome} {selected.cognome}</p>
              <div className="flex items-center gap-2 mt-0.5"><StatusBadge compiled={selected.compiled} /><span className="text-xs text-slate-400">sola consultazione</span></div>
            </div>
          </div>
          <Button size="sm" variant="outline" disabled={busy === selected.id} onClick={() => solicit(selected.id, setBusy)} data-testid="office-agenda-solicit-worker"
            className="rounded-lg border-[#D9D4F0] text-[#6D5FB8] hover:bg-[#EDEBF7]"><BellRing className="h-4 w-4 mr-1.5" /> Sollecita compilazione</Button>
        </div>
        {loading ? <p className="text-sm text-slate-500">Caricamento…</p> : <ReadOnlyAgenda slots={slots} absences={absences} highlightDow={highlightDow} />}
      </div>
    );
  }

  return (
    <div data-testid="office-agenda-worker-picker">
      <div className="mb-3"><SearchBox value={search} onChange={setSearch} testid="office-agenda-worker-search" placeholder="Cerca lavoratore…" /></div>
      <button onClick={() => setOnlyMissing((v) => !v)} data-testid="office-agenda-filter-missing"
        className={`mb-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold border transition-all ${onlyMissing ? "bg-amber-500 text-white border-amber-500" : "bg-white text-amber-700 border-amber-300 hover:bg-amber-50"}`}>
        <AlertCircle className="h-4 w-4" /> Solo Agenda non compilata{missingCount ? ` (${missingCount})` : ""}
      </button>
      {filtered.length === 0 ? (
        <Card className="rounded-xl border-slate-200 shadow-sm"><CardContent className="p-6 text-center text-slate-500 text-sm">{onlyMissing ? "Tutti i lavoratori hanno compilato l'Agenda." : "Nessun lavoratore."}</CardContent></Card>
      ) : (
        <div className="space-y-2.5">
          {filtered.map((w) => (
            <div key={w.id} className="rounded-xl bg-white border border-slate-200 shadow-sm p-4 flex items-center gap-3" data-testid={`office-agenda-worker-row-${w.id}`}>
              <button onClick={() => openWorker(w)} data-testid={`office-agenda-worker-${w.id}`} className="min-w-0 flex-1 text-left group">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold text-slate-800 truncate group-hover:text-primary transition-colors">{w.nome} {w.cognome}</p>
                  {w.updated && <UpdatedBadge />}
                </div>
                <div className="flex items-center gap-2 mt-1"><StatusBadge compiled={w.compiled} /><span className="text-xs text-slate-400 truncate">{w.ruolo_professionale || "Lavoratore"}</span></div>
              </button>
              {!w.compiled && (
                <Button size="sm" variant="ghost" disabled={busy === w.id} onClick={() => solicit(w.id, setBusy)} data-testid={`office-agenda-solicit-${w.id}`}
                  className="shrink-0 text-[#6D5FB8] hover:bg-[#EDEBF7] h-8"><BellRing className="h-4 w-4 mr-1.5" /> Sollecita</Button>
              )}
              <ChevronRight className="h-5 w-5 text-slate-400 shrink-0" onClick={() => openWorker(w)} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PerServizio() {
  const [service, setService] = useState("");
  const [inserted, setInserted] = useState([]);
  const [missing, setMissing] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  const services = Object.entries(SERVICES);

  const pick = async (key) => {
    setService(key); setLoading(true); setInserted([]); setMissing([]);
    try {
      const { data } = await api.get(`/office/agenda/by-service?service=${key}`);
      setInserted(data?.inserted || []); setMissing(data?.missing || []);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Errore"); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="office-agenda-by-service">
      <div className="flex flex-wrap gap-2 mb-5">
        {services.map(([slug, c]) => (
          <button key={slug} onClick={() => pick(c.key)} data-testid={`office-agenda-svc-${slug}`}
            className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${service === c.key ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"}`}>
            {c.label}
          </button>
        ))}
      </div>
      {!service ? (
        <p className="text-sm text-slate-500">Seleziona un servizio per vedere quali lavoratori lo hanno in agenda.</p>
      ) : loading ? (
        <p className="text-sm text-slate-500">Caricamento…</p>
      ) : (
        <div className="space-y-5">
          <div>
            <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-2 flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5" /> Hanno inserito il servizio ({inserted.length})</p>
            {inserted.length === 0 ? (
              <Card className="rounded-xl border-slate-200 shadow-sm"><CardContent className="p-6 text-center text-slate-500 text-sm" data-testid="office-agenda-svc-empty">Nessun lavoratore ha questo servizio in agenda.</CardContent></Card>
            ) : (
              <div className="space-y-3" data-testid="office-agenda-svc-results">
                {inserted.map((g) => (
                  <Card key={g.worker_id} className="rounded-xl border-slate-200 shadow-sm" data-testid={`office-agenda-svc-worker-${g.worker_id}`}>
                    <CardContent className="p-4">
                      <p className="font-semibold text-slate-800 mb-2">{g.worker_name}</p>
                      <div className="space-y-1.5">
                        {g.slots.map((s) => (
                          <div key={s.id} className="flex items-center gap-2 text-sm flex-wrap rounded-lg bg-slate-50 border border-slate-100 px-3 py-1.5">
                            <Badge className="bg-primary/10 text-primary hover:bg-primary/10 border-0 text-[10px]">{DAYS[s.day_of_week]}</Badge>
                            <span className="inline-flex items-center gap-1 font-medium text-slate-700"><Clock className="h-3.5 w-3.5 text-slate-400" />{s.start_time}–{s.end_time}</span>
                            {s.role_or_task && <span className="text-slate-500 text-xs">· {s.role_or_task}</span>}
                            <span className="text-slate-500 text-xs truncate">· {s.titolo}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
          {missing.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-2 flex items-center gap-1.5"><AlertCircle className="h-3.5 w-3.5" /> Pertinenti ma non inseriti ({missing.length})</p>
              <div className="space-y-2" data-testid="office-agenda-svc-missing">
                {missing.map((m) => (
                  <div key={m.worker_id} className="rounded-xl bg-white border border-amber-200 shadow-sm p-3 flex items-center gap-3" data-testid={`office-agenda-svc-missing-${m.worker_id}`}>
                    <span className="min-w-0 flex-1 font-medium text-slate-800 truncate">{m.worker_name}</span>
                    <Button size="sm" variant="ghost" disabled={busy === m.worker_id} onClick={() => solicit(m.worker_id, setBusy)} data-testid={`office-agenda-svc-solicit-${m.worker_id}`}
                      className="shrink-0 text-[#6D5FB8] hover:bg-[#EDEBF7] h-8"><BellRing className="h-4 w-4 mr-1.5" /> Sollecita</Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function AgendeSettimanaliSection({ onSeen, deepLink, onDeepLinkDone }) {
  const [mode, setMode] = useState("lavoratore");
  useEffect(() => { if (deepLink?.workerId) setMode("lavoratore"); }, [deepLink]);
  return (
    <div data-testid="agende-settimanali">
      <div className="flex gap-2 mb-5">
        <TabPill active={mode === "lavoratore"} onClick={() => setMode("lavoratore")} testid="office-agenda-tab-lavoratore">Per lavoratore</TabPill>
        <TabPill active={mode === "servizio"} onClick={() => setMode("servizio")} testid="office-agenda-tab-servizio">Per servizio</TabPill>
      </div>
      {mode === "lavoratore" ? <PerLavoratore onSeen={onSeen} deepLink={deepLink} onDeepLinkDone={onDeepLinkDone} /> : <PerServizio />}
    </div>
  );
}
